import React, { useEffect, useState } from "react";
import { useClientInfo, SnackbarAlert } from "../../utils/commonImports";
import axiosInstance from "../../api/axiosInstance";

const DEFAULT_FOOTER =
  "This is a computer-generated invoice and does not require a physical signature.";

const initialForm = {
  invoice_title: "Invoice",
  enable_print: true,
  invoice_cover_full_page: true,
  invoice_copies: 1,
  print_copy_name_on_invoice: true,

  local_invoice_template_id: "",
  contract_invoice_template_id: "",
  direct_contract_invoice_template_id: "",
  misc_invoice_template_id: "",
  proforma_invoice_template_id: "",

  post_separate_ledgers: false,

  signature_file: null,
  round_seal_file: null,

  dsc_enabled: false,
  dsc_certificate_identifier: "",
  dsc_provider: "",
  dsc_signing_method: "USB_TOKEN",

  signatory: "AUTHORIZED_SIGNATORY",
  signatory_name: "",

  show_bank_details: true,
  number_of_bank_details: 1,

  show_gst_details: true,
  show_payment_details: true,
  show_qr_code: false,

  invoice_rounding: "NONE",

  terms_conditions: "",

  footer_text: DEFAULT_FOOTER,
};

function SettingsSection({ number, title, description, children }) {
  return (
    <section className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center text-sm font-bold">
            {number}
          </div>

          <div>
            <h3 className="text-base font-semibold text-gray-800">{title}</h3>

            {description && (
              <p className="text-xs text-gray-500 mt-0.5">{description}</p>
            )}
          </div>
        </div>
      </div>

      <div className="p-6">{children}</div>
    </section>
  );
}

function Field({ label, required = false, children, hint }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {children}

      {hint && <p className="text-xs text-gray-500 mt-1.5">{hint}</p>}
    </div>
  );
}

function ToggleRow({ label, description, checked, onChange }) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="pr-6">
        <div className="text-sm font-medium text-gray-800">{label}</div>

        {description && (
          <div className="text-xs text-gray-500 mt-0.5">{description}</div>
        )}
      </div>

      <button
        type="button"
        onClick={onChange}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition ${
          checked ? "bg-orange-500" : "bg-gray-300"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
            checked ? "translate-x-5" : "translate-x-1"
          }`}
        />
      </button>
    </div>
  );
}

function FileUploadBox({
  title,
  description,
  value,
  onChange,
  accept = "image/png,image/jpeg",
}) {
  return (
    <div className="border-2 border-dashed border-gray-300 rounded-xl p-5 hover:border-orange-400 transition">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-gray-800">{title}</div>

          <div className="text-xs text-gray-500 mt-1">{description}</div>
        </div>

        <label className="cursor-pointer inline-flex items-center px-4 py-2 rounded-lg bg-orange-50 text-orange-700 text-sm font-medium hover:bg-orange-100">
          Choose File
          <input
            type="file"
            accept={accept}
            className="hidden"
            onChange={onChange}
          />
        </label>
      </div>

      {value && (
        <div className="mt-4 p-3 rounded-lg bg-gray-50 border border-gray-200">
          <div className="flex items-center gap-3">
            <img
              src={value}
              alt={title}
              className="h-16 max-w-[180px] object-contain bg-white border border-gray-200 rounded"
            />

            <div className="text-xs text-green-600 font-medium">
              File selected
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function InvoiceSettingsForm() {
  const { clientId } = useClientInfo();

  const [form, setForm] = useState(initialForm);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleToggle = (name) => {
    setForm((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const handleFileChange = (field, e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      SnackbarAlert("Please select a valid image file.", "error");
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      setForm((prev) => ({
        ...prev,
        [field]: reader.result,
      }));
    };

    reader.readAsDataURL(file);
  };

  /*
  |--------------------------------------------------------------------------
  | Load Invoice Settings
  |--------------------------------------------------------------------------
  */
  useEffect(() => {
    if (!clientId) return;

    const loadSettings = async () => {
      try {
        setLoading(true);

        const response = await axiosInstance.get(
          `/api/clients/${clientId}/invoice-settings`,
        );

        const data = response.data;

        if (data.invoice) {
          const invoice = data.invoice;

          setForm({
            ...initialForm,
            ...invoice,

            local_invoice_template_id: invoice.local_invoice_template_id ?? "",

            contract_invoice_template_id:
              invoice.contract_invoice_template_id ?? "",

            direct_contract_invoice_template_id:
              invoice.direct_contract_invoice_template_id ?? "",

            misc_invoice_template_id: invoice.misc_invoice_template_id ?? "",

            proforma_invoice_template_id:
              invoice.proforma_invoice_template_id ?? "",

            signature_file: invoice.signature_file ?? null,

            round_seal_file: invoice.round_seal_file ?? null,

            dsc_certificate_identifier:
              invoice.dsc_certificate_identifier ?? "",

            dsc_provider: invoice.dsc_provider ?? "",

            dsc_signing_method: invoice.dsc_signing_method ?? "USB_TOKEN",

            signatory_name: invoice.signatory_name ?? "",

            terms_conditions: invoice.terms_conditions ?? "",

            footer_text: invoice.footer_text || DEFAULT_FOOTER,
          });
        }
      } catch (error) {
        console.error("Error loading Invoice settings:", error);

        SnackbarAlert(
          error.response?.data?.error ||
            error.message ||
            "Unable to load Invoice settings.",
          "error",
        );
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [clientId]);

  /*
  |--------------------------------------------------------------------------
  | Load Common Invoice Templates
  |--------------------------------------------------------------------------
  */
  useEffect(() => {
    if (!clientId) return;

    const loadTemplates = async () => {
      try {
        setTemplatesLoading(true);

        const response = await axiosInstance.get(
          `/api/clients/${clientId}/invoice-templates`,
        );

        const data = response.data;

        setTemplates(data.templates || []);
      } catch (error) {
        console.error("Error loading Invoice templates:", error);

        SnackbarAlert(
          error.response?.data?.error ||
            error.message ||
            "Unable to load Invoice templates.",
          "error",
        );
      } finally {
        setTemplatesLoading(false);
      }
    };

    loadTemplates();
  }, [clientId]);

  /*
  |--------------------------------------------------------------------------
  | Save
  |--------------------------------------------------------------------------
  */
  const handleSave = async () => {
    if (!clientId) {
      SnackbarAlert("Client information is not available.", "error");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        ...form,

        invoice_copies: Number(form.invoice_copies || 1),

        number_of_bank_details: Number(form.number_of_bank_details || 1),

        local_invoice_template_id: form.local_invoice_template_id || null,

        contract_invoice_template_id: form.contract_invoice_template_id || null,

        direct_contract_invoice_template_id:
          form.direct_contract_invoice_template_id || null,

        misc_invoice_template_id: form.misc_invoice_template_id || null,

        proforma_invoice_template_id: form.proforma_invoice_template_id || null,

        signatory_name: form.signatory_name || null,

        dsc_certificate_identifier:
  form.dsc_certificate_identifier || null,

dsc_provider: form.dsc_provider || null,

dsc_signing_method:
  form.dsc_signing_method || "USB_TOKEN",


        terms_conditions: form.terms_conditions || null,
      };

      const response = await axiosInstance.post(
        `/api/clients/${clientId}/invoice-settings`,
        payload,
      );

      const data = response.data;

      SnackbarAlert("Invoice settings saved successfully.", "success");
    } catch (error) {
      console.error("Error saving Invoice settings:", error);

      SnackbarAlert(
        error.message || "Unable to save Invoice settings.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const getTemplates = (documentType) => {
    return templates.filter(
      (template) => template.document_type === documentType,
    );
  };

  const renderTemplateSelect = (label, documentType, fieldName) => {
    const options = getTemplates(documentType);

    return (
      <Field
        label={label}
        hint={
          templatesLoading
            ? "Loading templates..."
            : options.length === 0
              ? "No templates available for this invoice type."
              : "Select the template to use for this invoice."
        }
      >
        <select
          name={fieldName}
          value={form[fieldName] ?? ""}
          onChange={handleChange}
          disabled={templatesLoading}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100 disabled:bg-gray-100"
        >
          <option value="">
            {templatesLoading
              ? "Loading templates..."
              : "Select Invoice Template"}
          </option>

          {options.map((template) => (
            <option key={template.id} value={template.id}>
              {template.template_name}
            </option>
          ))}
        </select>
      </Field>
    );
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="text-sm text-gray-500">Loading Invoice settings...</div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-full">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center text-xl">
            🧾
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              Invoice Settings
            </h2>

            <p className="text-sm text-gray-500 mt-1">
              Configure invoice formats, templates, printing, signing and
              display options.
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-5 max-w-6xl">
        {/* 01 GENERAL */}
        <SettingsSection
          number="01"
          title="General"
          description="Basic invoice configuration"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field
              label="Invoice Title"
              required
              hint="This title will be used on the invoice."
            >
              <input
                type="text"
                name="invoice_title"
                value={form.invoice_title}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-3 text-lg font-bold text-gray-800 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                placeholder="Invoice"
              />
            </Field>
          </div>

          <div className="mt-5 divide-y divide-gray-100">
            <ToggleRow
              label="Enable Invoice Printing"
              description="Allow invoices to be printed from the system."
              checked={form.enable_print}
              onChange={() => handleToggle("enable_print")}
            />

            <ToggleRow
              label="Invoice Covers Full Page"
              description="Format the invoice to occupy the full printable page."
              checked={form.invoice_cover_full_page}
              onChange={() => handleToggle("invoice_cover_full_page")}
            />
          </div>
        </SettingsSection>

        {/* 02 TEMPLATES */}
        <SettingsSection
          number="02"
          title="Invoice Templates"
          description="Select a common document template for each invoice type"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {renderTemplateSelect(
              "Local Invoice Template",
              "LOCAL_INVOICE",
              "local_invoice_template_id",
            )}

            {renderTemplateSelect(
              "Contract Invoice Template",
              "CONTRACT_INVOICE",
              "contract_invoice_template_id",
            )}

            {renderTemplateSelect(
              "Direct Contract Invoice Template",
              "DIRECT_CONTRACT_INVOICE",
              "direct_contract_invoice_template_id",
            )}

            {renderTemplateSelect(
              "Misc Invoice Template",
              "MISC_INVOICE",
              "misc_invoice_template_id",
            )}

            {renderTemplateSelect(
              "Proforma Invoice Template",
              "PROFORMA_INVOICE",
              "proforma_invoice_template_id",
            )}
          </div>
        </SettingsSection>

        {/* 03 POSTING */}
        <SettingsSection
          number="03"
          title="Posting Controls"
          description="Control how additional invoice charges are posted"
        >
          <ToggleRow
            label="Post Separate Hire Charges, Parking, Batta, Permit & Toll Ledgers"
            description="Post these components separately instead of combining them into a single hire charge."
            checked={form.post_separate_ledgers}
            onChange={() => handleToggle("post_separate_ledgers")}
          />
        </SettingsSection>

        {/* 04 COPIES */}
        <SettingsSection
          number="04"
          title="Invoice Copies"
          description="Control the number of invoice copies and whether the copy name is shown"
        >
          <div className="max-w-md">
            <Field
              label="Number of Invoice Copies to Print"
              hint="The system automatically identifies copies as Original, Duplicate, Triplicate and Quadruplicate."
            >
              <input
                type="number"
                min="1"
                max="4"
                name="invoice_copies"
                value={form.invoice_copies}
                onChange={(e) => {
                  const value = Math.max(
                    1,
                    Math.min(4, Number(e.target.value || 1)),
                  );

                  setForm((prev) => ({
                    ...prev,
                    invoice_copies: value,
                  }));
                }}
                className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
              />
            </Field>
          </div>

          <div className="mt-5 border-t border-gray-100 pt-2">
            <ToggleRow
              label="Print Copy Name on Invoice"
              description="When enabled, the system automatically prints ORIGINAL, DUPLICATE, TRIPLICATE or QUADRUPLICATE according to the copy number."
              checked={form.print_copy_name_on_invoice}
              onChange={() => handleToggle("print_copy_name_on_invoice")}
            />
          </div>
        </SettingsSection>

        {/* 05 SIGNATURE */}
        <SettingsSection
          number="05"
          title="Signature, Seal & Digital Signing"
          description="Configure signature, round seal and DSC for PDF invoices"
        >
          {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FileUploadBox
              title="Upload Signature"
              description="Signature will appear on PDF invoices."
              value={form.signature_file}
              onChange={(e) => handleFileChange("signature_file", e)}
            />

            <FileUploadBox
              title="Upload Round Seal"
              description="Round seal will appear on PDF invoices."
              value={form.round_seal_file}
              onChange={(e) => handleFileChange("round_seal_file", e)}
            />
          </div> */}

          <div className="mt-5 border-t border-gray-100 pt-5">
            <ToggleRow
              label="Enable Digital Signature (DSC)"
              description="Enable digital signing of generated PDF invoices using a DSC."
              checked={form.dsc_enabled}
              onChange={() => handleToggle("dsc_enabled")}
            />

            
{form.dsc_enabled && (
  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-5">
    <Field label="DSC Provider" required>
      <select
        name="dsc_provider"
        value={form.dsc_provider}
        onChange={handleChange}
        required
        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
      >
        <option value="">Select DSC Provider</option>
        <option value="EMUDHRA">eMudhra</option>
        <option value="CAPRICORN">Capricorn</option>
        <option value="PROXKEY">ProxKey</option>
        <option value="OTHER">Other</option>
      </select>
    </Field>

    <Field label="Signing Method" required>
      <select
        name="dsc_signing_method"
        value={form.dsc_signing_method}
        onChange={handleChange}
        required
        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
      >
        <option value="USB_TOKEN">USB DSC Token</option>
        <option value="REMOTE_SIGNING">Remote Signing Service</option>
      </select>
    </Field>

    <div className="md:col-span-2">
      <Field
        label="DSC Certificate Identifier"
        hint="Enter a certificate name or identifier. Never store the token PIN, password, or private key here."
      >
        <input
          type="text"
          name="dsc_certificate_identifier"
          value={form.dsc_certificate_identifier}
          onChange={handleChange}
          className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
          placeholder="Enter certificate identifier"
        />
      </Field>
    </div>
  </div>
)}


          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Signatory">
              <select
                name="signatory"
                value={form.signatory}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
              >
                <option value="AUTHORIZED_SIGNATORY">
                  Authorized Signatory
                </option>
                <option value="PROPRIETOR">Proprietor</option>
                <option value="DIRECTOR">Director</option>
                <option value="PARTNER">Partner</option>
                <option value="ACCOUNT_MANAGER">Account Manager</option>
              </select>
            </Field>

            <Field
              label="Signatory Name"
              hint="Name printed below the signature."
            >
              <input
                type="text"
                name="signatory_name"
                value={form.signatory_name}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                placeholder="Enter signatory name"
              />
            </Field>
          </div>

          <div className="mt-5 p-4 rounded-lg bg-orange-50 border border-orange-100">
            <p className="text-xs text-orange-800">
              <strong>Printing rule:</strong> Signature and round seal will be
              excluded from physical print output and will appear on PDF
              invoices. DSC is used for digitally signing the PDF.
            </p>
          </div>
        </SettingsSection>

        {/* 06 BANK */}
        <SettingsSection
          number="06"
          title="Bank Details"
          description="Control the number of client bank accounts displayed on the invoice"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field
              label="Show Bank Details"
              hint="Display the client's bank details on the invoice."
            >
              <select
                name="show_bank_details"
                value={form.show_bank_details ? "YES" : "NO"}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    show_bank_details: e.target.value === "YES",
                  }))
                }
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
              >
                <option value="YES">Yes</option>
                <option value="NO">No</option>
              </select>
            </Field>

            <Field
              label="Number of Bank Details to Show"
              hint="Select one or two bank accounts from the client's Bank Details master."
            >
              <select
                name="number_of_bank_details"
                value={form.number_of_bank_details}
                onChange={handleChange}
                disabled={!form.show_bank_details}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100 disabled:bg-gray-100"
              >
                <option value={1}>One Bank</option>
                <option value={2}>Two Banks</option>
              </select>
            </Field>
          </div>
        </SettingsSection>

        {/* 07 PRINT CONTROLS */}
        <SettingsSection
          number="07"
          title="Additional Print Controls"
          description="Choose which information appears on the invoice"
        >
          <div className="divide-y divide-gray-100">
            <ToggleRow
              label="Show GST Details"
              description="Display GST information on the invoice."
              checked={form.show_gst_details}
              onChange={() => handleToggle("show_gst_details")}
            />

            <ToggleRow
              label="Show Payment Details"
              description="Display payment and payment-term information."
              checked={form.show_payment_details}
              onChange={() => handleToggle("show_payment_details")}
            />

            <ToggleRow
              label="Show QR Code"
              description="Display the configured payment QR code."
              checked={form.show_qr_code}
              onChange={() => handleToggle("show_qr_code")}
            />
          </div>
        </SettingsSection>

        {/* 08 ROUNDING */}
        <SettingsSection
          number="08"
          title="Invoice Calculation"
          description="Configure invoice total rounding"
        >
          <div className="max-w-md">
            <Field
              label="Invoice Rounding"
              hint="Controls rounding applied to the final invoice amount."
            >
              <select
                name="invoice_rounding"
                value={form.invoice_rounding}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
              >
                <option value="NONE">No Rounding</option>
                <option value="NEAREST_RUPEE">Round to Nearest Rupee</option>
                <option value="ROUND_DOWN">Round Down</option>
                <option value="ROUND_UP">Round Up</option>
              </select>
            </Field>
          </div>
        </SettingsSection>

        {/* 09 TERMS */}
        <SettingsSection
          number="09"
          title="Terms & Conditions"
          description="Terms displayed on the invoice"
        >
          <Field
            label="Terms & Conditions"
            hint="These terms can be printed at the bottom of the invoice."
          >
            <textarea
              name="terms_conditions"
              value={form.terms_conditions}
              onChange={handleChange}
              rows={7}
              className="w-full rounded-lg border border-gray-300 px-3 py-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100 resize-y"
              placeholder="Enter invoice terms and conditions..."
            />
          </Field>
        </SettingsSection>

        {/* 10 FOOTER */}
        <SettingsSection
          number="10"
          title="Invoice Footer"
          description="Text shown at the bottom of the invoice"
        >
          <Field
            label="Footer Text"
            hint="This text is printed/displayed according to the selected invoice template."
          >
            <textarea
              name="footer_text"
              value={form.footer_text}
              onChange={handleChange}
              rows={3}
              className="w-full rounded-lg border border-gray-300 px-3 py-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100 resize-y"
            />
          </Field>
        </SettingsSection>

        {/* SAVE */}
        <div className="sticky bottom-0 bg-white border border-gray-200 rounded-xl shadow-lg px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-gray-800">
              Invoice Settings
            </div>

            <div className="text-xs text-gray-500">
              Save your changes to apply them to future invoices.
            </div>
          </div>

          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2.5 rounded-lg bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>
    </div>
  );
}
